import React, { useEffect, useState } from "react"
import { Button, Flex, Switch } from "antd"
import { useDispatch } from "react-redux"
import { spacings } from "themes/variables"
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons"
import { Card } from "ui/Card"
import { Text } from "ui/Typography"
import IconWithBg from "ui/IconWithBg"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { NewConfirmModal } from "ui/NewConfirmModal"
import { updateTrip } from "store/trips"
import { buildAddOnsPayload } from "../../../mappers/toApiPayload"
import { mapServerAddOn } from "../../../mappers/buildDefaultValues"
import { CreateAddOnModal } from "../CreateAddOnModal/CreateAddOnModal"
import { useWizardContext } from "../../../WizardContext"
import styles from "./AddOns.module.css"

export const AddOns = () => {
  const dispatch = useDispatch()
  const { form, trip, isFormInitialized } = useWizardContext()
  const [addOns, setAddOns] = useState(() => form.getFieldValue("add_ons") || [])

  useEffect(() => {
    if (isFormInitialized) {
      setAddOns(form.getFieldValue("add_ons") || [])
    }
  }, [isFormInitialized, form])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isAddOnSaving, setIsAddOnSaving] = useState(false)
  const [editingAddOn, setEditingAddOn] = useState(null)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [pendingDeactivate, setPendingDeactivate] = useState(null)

  // Writes to both local state (re-render) and form store (persistence) in one step.
  // Avoids useEffect sync which can miss the unmount window on fast tab switches.
  const setAndSync = (updater) => {
    setAddOns((prev) => {
      const next = updater(prev)
      form.setFieldValue("add_ons", next)
      return next
    })
  }

  const handleSave = async (addOn) => {
    const nextAddOns = editingAddOn
      ? addOns.map((a) => (a.id === editingAddOn.id ? { ...a, ...addOn } : a))
      : [...addOns, { id: Date.now(), enabled: true, ...addOn }]

    if (trip?.id) {
      // Only update local state after the API confirms success.
      // On error the api() helper shows a notification and the list stays unchanged.
      try {
        setIsAddOnSaving(true)
        const payload = buildAddOnsPayload(nextAddOns, [])
        const response = await dispatch(updateTrip(trip.id, payload))
        const serverAddOns = response?.add_ons
        const synced = serverAddOns?.length ? serverAddOns.map(mapServerAddOn) : nextAddOns
        setAddOns(synced)
        form.setFieldValue("add_ons", synced)
      } finally {
        setIsAddOnSaving(false)
      }
    } else {
      // No trip yet (new experience) — persist locally only; saved on step navigation.
      setAddOns(nextAddOns)
      form.setFieldValue("add_ons", nextAddOns)
    }
  }

  const handleEdit = (addOn) => {
    setEditingAddOn(addOn)
    setIsModalOpen(true)
  }

  const handleDeleteRequest = (id) => setPendingDelete(id)

  const handleDeleteConfirm = () => {
    setAndSync((prev) => prev.filter((a) => a.id !== pendingDelete))
    setPendingDelete(null)
  }

  const handleToggle = (id, checked) => {
    if (checked) {
      setAndSync((prev) => prev.map((a) => (a.id === id ? { ...a, enabled: true } : a)))
    } else {
      setPendingDeactivate(id)
    }
  }

  const handleDeactivateConfirm = () => {
    setAndSync((prev) => prev.map((a) => (a.id === pendingDeactivate ? { ...a, enabled: false } : a)))
    setPendingDeactivate(null)
  }

  const handleOpenCreate = () => {
    setEditingAddOn(null)
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setEditingAddOn(null)
    setIsModalOpen(false)
  }

  const deleteTarget = addOns.find((a) => a.id === pendingDelete)
  const deactivateTarget = addOns.find((a) => a.id === pendingDeactivate)

  return (
    <>
      <Card
        title={"Add-Ons"}
        className="mb-xl"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate} className={styles.createBtn}>
            {"Create New Add-on"}
          </Button>
        }
      >
        <WithEmptyStateWrapper name="add-on" hasItems={addOns.length > 0} fallbackTitle={"No add-ons yet"}>
          <Flex vertical gap={spacings.XS}>
            {addOns.map((addOn) => (
              <Flex key={addOn.id} align="center" gap={spacings.S} className={styles.row}>
                <Switch checked={addOn.enabled} onChange={(checked) => handleToggle(addOn.id, checked)} />
                {addOn.photo ? (
                  <img src={addOn.photo} alt={addOn.title} className={styles.thumbnail} />
                ) : (
                  <div className={styles.thumbnailPlaceholder} />
                )}
                <Flex vertical gap={spacings.XXS} className={styles.info}>
                  <Text strong>{addOn.title}</Text>
                  <Text className={styles.infoCategory}>{addOn.categoryName ?? addOn.category}</Text>
                </Flex>
                <Flex align="center" gap={spacings.S} className={styles.rowRight}>
                  {(addOn.minPerOrder != null || addOn.maxPerOrder != null) && (
                    <Text className={styles.rangeText}>
                      {`Min ${addOn.minPerOrder ?? 0} - max ${addOn.maxPerOrder ?? "∞"}`}
                    </Text>
                  )}
                  <Text strong className={addOn.enabled ? styles.priceActive : styles.priceInactive}>
                    {`$${Number(addOn.price ?? 0).toFixed(2)}`}
                  </Text>
                </Flex>
                <IconWithBg icon={<EditOutlined />} onClick={() => handleEdit(addOn)} />
                <IconWithBg icon={<DeleteOutlined />} onClick={() => handleDeleteRequest(addOn.id)} />
              </Flex>
            ))}
          </Flex>
        </WithEmptyStateWrapper>
      </Card>

      <CreateAddOnModal
        open={isModalOpen}
        onCancel={handleModalClose}
        onSave={handleSave}
        isSaving={isAddOnSaving}
        initialValues={editingAddOn}
      />

      <NewConfirmModal
        visible={!!pendingDeactivate}
        onClose={() => setPendingDeactivate(null)}
        onConfirm={handleDeactivateConfirm}
        title={"Deactivate Add-On"}
        message={"Are you sure you want to deactivate:"}
        highlightText={deactivateTarget?.title}
        confirmLabel={"Deactivate"}
      />

      <NewConfirmModal
        visible={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDeleteConfirm}
        title={"Delete Add-On"}
        message={"Are you sure you want to delete:"}
        highlightText={deleteTarget?.title}
        confirmLabel={"Delete"}
      />
    </>
  )
}
